import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SealCheck, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EMPANELMENT_STATUS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CHECKLIST = ["Application form", "Valuer registration certificate", "PAN & GST certificate", "Firm profile / CV", "Past work samples", "Fee schedule agreed"];

const today = () => new Date().toISOString().slice(0, 10);
const daysTo = (d) => Math.round((new Date(d) - new Date(today())) / 86400000);

export default function Empanelment() {
  const [branches, setBranches] = useState(null);
  const [checklist, setChecklist] = useState({});

  const load = () => api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
  useEffect(() => {
    load();
    api.get("/settings/empanelment_checklist").then((r) => setChecklist(r.data?.items || {})).catch(() => {});
  }, []);

  const patch = async (b, fields) => {
    try {
      await api.put(`/branches/${b.id}`, { ...b, ...fields });
      toast.success("Empanelment updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleCheck = async (branchId, item) => {
    const cur = checklist[branchId] || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    const merged = { ...checklist, [branchId]: next };
    setChecklist(merged);
    try { await api.put("/settings/empanelment_checklist", { items: merged }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!branches) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading empanelment tracker…</div>;

  const tracked = branches.filter((b) => b.empanelment_status && b.empanelment_status !== "not_empanelled");
  const expiring = tracked.filter((b) => b.empanelment_renewal_due && daysTo(b.empanelment_renewal_due) <= 60);
  const expired = tracked.filter((b) => b.empanelment_renewal_due && daysTo(b.empanelment_renewal_due) < 0);
  const focus = expiring.length ? expiring : tracked;

  return (
    <div className="space-y-6" data-testid="empanelment-page">
      <PageHeader overline="Bank Branches" title="Empanelment Tracker" subtitle="Never let an approval lapse — approvals are what make a branch eligible to assign work" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Tracked" value={tracked.length} testid="stat-tracked" />
        <Stat label="Active" value={tracked.filter((b) => b.empanelment_status === "active").length} tone="text-emerald-600" testid="stat-active" />
        <Stat label="Expiring ≤60d" value={expiring.length - expired.length} tone="text-amber-600" testid="stat-expiring" />
        <Stat label="Expired" value={expired.length} tone="text-rose-600" testid="stat-expired" />
      </div>

      <Card className="border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-display text-lg font-semibold text-primary">Set empanelment details</h3>
          <p className="text-xs text-muted-foreground">Update the status, category and renewal date for any branch.</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wide">Branch</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Category</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Renewal Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b) => {
                const d = b.empanelment_renewal_due ? daysTo(b.empanelment_renewal_due) : null;
                return (
                  <TableRow key={b.id} className="hover:bg-muted/40" data-testid={`emp-row-${b.id}`}>
                    <TableCell className="max-w-[240px]">
                      <Link to={`/app/branches/${b.id}`} className="text-sm font-medium text-primary hover:text-accent">{b.bank_name}</Link>
                      <div className="text-xs text-muted-foreground truncate">{b.branch_name}</div>
                    </TableCell>
                    <TableCell>
                      <Select value={b.empanelment_status || "not_empanelled"} onValueChange={(v) => patch(b, { empanelment_status: v })}>
                        <SelectTrigger className="h-8 text-xs w-[150px]" data-testid={`emp-status-${b.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(EMPANELMENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input defaultValue={b.empanelment_category || ""} onBlur={(e) => e.target.value !== (b.empanelment_category || "") && patch(b, { empanelment_category: e.target.value })}
                        placeholder="e.g. Category A" className="h-8 text-xs w-[150px]" data-testid={`emp-cat-${b.id}`} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DatePicker value={(b.empanelment_renewal_due || "").slice(0, 10)}
                          onChange={(v) => patch(b, { empanelment_renewal_due: v || null })}
                          placeholder="Set date" clearable className="w-[150px]" data-testid={`emp-due-${b.id}`} />
                        {d != null && (
                          <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${d < 0 ? "bg-rose-100 text-rose-700 border-rose-200" : d <= 60 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}`}>
                            {d < 0 ? `${-d}d overdue` : `${d}d left`}
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <h3 className="font-display text-lg font-semibold text-primary">Renewal Checklist</h3>
        <p className="text-xs text-muted-foreground mb-4">Tick off documents as you collect them for each branch coming up for renewal.</p>
        {focus.length === 0 ? (
          <EmptyState icon={SealCheck}>Set an empanelment status and renewal date above to start tracking a checklist.</EmptyState>
        ) : (
          <div className="space-y-4">
            {focus.slice(0, 8).map((b) => {
              const done = checklist[b.id] || [];
              return (
                <div key={b.id} className="rounded-md border border-border p-4" data-testid={`checklist-${b.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-primary">{b.bank_name} — {b.branch_name}</div>
                    <span className="text-xs text-muted-foreground">{done.length}/{CHECKLIST.length} done</span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {CHECKLIST.map((item) => (
                      <button key={item} onClick={() => toggleCheck(b.id, item)}
                        className="flex items-center gap-2 text-left text-sm p-1.5 rounded hover:bg-muted transition-colors duration-150"
                        data-testid={`check-${b.id}-${item.replace(/\W+/g, "-")}`}>
                        <CheckCircle size={17} weight={done.includes(item) ? "fill" : "regular"} className={done.includes(item) ? "text-emerald-500" : "text-muted-foreground"} />
                        <span className={done.includes(item) ? "line-through text-muted-foreground" : "text-primary"}>{item}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
